import axios from 'axios'

// -------------------------------------------------------------------
// MAIN API – Backend API (cookies ON, tenant header controlled)
// -------------------------------------------------------------------
const api = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL,
  withCredentials: true, // ✅ REQUIRED: always send cookies
})


// -------------------------------------------------------------------
// EXTERNAL API – Public APIs (no cookies, no tenant)
// -------------------------------------------------------------------
const externalApi = axios.create({
  withCredentials: false,
})


// -------------------------------------------------------------------
// Helper: determine if URL is external
// -------------------------------------------------------------------
const isExternal = (url) => {
  return url.startsWith('http://') || url.startsWith('https://')
}


// -------------------------------------------------------------------
// Cookie reader (CSRF support)
// -------------------------------------------------------------------
const getCookie = (name) => {
  if (document.cookie && document.cookie !== '') {
    const cookies = document.cookie.split(';')
    for (let i = 0; i < cookies.length; i++) {
      const cookie = cookies[i].trim()
      if (cookie.startsWith(name + '=')) {
        return decodeURIComponent(cookie.substring(name.length + 1))
      }
    }
  }
  return null
}


// -------------------------------------------------------------------
// Helper: resolve tenant subdomain SAFELY
// -------------------------------------------------------------------
const resolveTenantSubdomain = () => {
  const isDev = import.meta.env.DEV;

  // Default invalid tenants (prod-safe)
  const invalidTenants = new Set(['app', 'www', 'api']);

  // Dev-only additions (never bundled into prod)
  if (isDev) {
    invalidTenants.add('staging-app');
    invalidTenants.add('staging-api');
    invalidTenants.add('localhost');
  }

  // ❌ DO NOT derive tenant from hostname in prod
  if (isDev) {
    const hostname = window.location.hostname.toLowerCase();
    const sub = hostname.split('.')[0];
    if (!invalidTenants.has(sub)) {
      return sub;
    }
  }

  // ✅ Explicit tenant selection ONLY
  return (
    localStorage.getItem('activeTenantSubdomain') ||
    localStorage.getItem('tenantSubdomain') ||
    null
  );
};


// -------------------------------------------------------------------
// REQUEST INTERCEPTOR – Cookies, Tenant, CSRF
// -------------------------------------------------------------------
api.interceptors.request.use((config) => {
  // Resolve the latest JWT token from localStorage and apply it
  const token = localStorage.getItem('token');
  // Only set Authorization when token is a non-empty, non-'undefined', non-'null' string
  if (token && token !== 'undefined' && token !== 'null') {
    config.headers['Authorization'] = `Bearer ${token}`;
  } else {
    // ensure old header is not sent
    delete config.headers['Authorization'];
  }

  // Determine the tenant subdomain using the helper. In dev this may derive from hostname;
  // in production it returns the explicit selection stored in localStorage.
  const tenantSubdomain = resolveTenantSubdomain();
  if (tenantSubdomain) {
    config.headers['X-Tenant-Subdomain'] = tenantSubdomain;
  } else {
    // If no tenant subdomain (very unlikely), ensure the header is removed
    delete config.headers['X-Tenant-Subdomain'];
  }

  // CSRF token logic remains unchanged...
  const csrfToken = getCookie('XSRF-TOKEN');
  if (
    csrfToken &&
    !['GET', 'HEAD', 'OPTIONS'].includes(config.method.toUpperCase())
  ) {
    config.headers['X-XSRF-TOKEN'] = csrfToken;
  }

  return config;
});


// -------------------------------------------------------------------
// RESPONSE INTERCEPTOR – SAFE 401 HANDLING
// -------------------------------------------------------------------
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status
    const url = error?.config?.url || ''

    /**
     * 🚨 Only force logout if SESSION CHECK fails.
     * Do NOT logout on random 401s (e.g. tenant mismatch, permissions).
     */
    if (status === 401 && url.includes('/auth/me')) {
      const currentPath = window.location.pathname + window.location.search
      const event = new CustomEvent('unauthorized-redirect', {
        detail: { nextPath: currentPath },
      })
      window.dispatchEvent(event)
    }

    return Promise.reject(error)
  },
)


// -------------------------------------------------------------------
// MAIN EXPORT FUNCTIONS – SAME API (NO BREAKING CHANGES)
// -------------------------------------------------------------------
export const GetData = async (endPoint, options = {}) => {
  const client = isExternal(endPoint) ? externalApi : api
  return client.get(endPoint, options)
}

export const PostData = async (endPoint, options = {}) => {
  const client = isExternal(endPoint) ? externalApi : api
  return client.post(endPoint, options)
}

export const PutData = async (endPoint, options = {}) => {
  const client = isExternal(endPoint) ? externalApi : api
  return client.put(endPoint, options)
}

export const PatchData = async (endPoint, options = {}) => {
  const client = isExternal(endPoint) ? externalApi : api
  return client.patch(endPoint, options)
}

export const DeleteData = async (endPoint, options = {}) => {
  const client = isExternal(endPoint) ? externalApi : api
  return client.delete(endPoint, options)
}


// -------------------------------------------------------------------
// DOWNLOAD DATA (unchanged behavior)
// -------------------------------------------------------------------
export const DownloadData = async (
  endPoint,
  filename = 'download',
  fallbackMimeType = 'application/octet-stream',
) => {
  try {
    const client = isExternal(endPoint) ? externalApi : api
    const response = await client.get(endPoint, { responseType: 'blob' })

    const mimeType = response.headers['content-type'] || fallbackMimeType
    const extension = getFileExtensionFromMimeType(mimeType)

    const blob = new Blob([response.data], { type: mimeType })
    const url = window.URL.createObjectURL(blob)

    const a = document.createElement('a')
    a.href = url
    a.download = filename.endsWith(`.${extension}`)
      ? filename
      : `${filename}.${extension}`
    document.body.appendChild(a)
    a.click()
    a.remove()

    window.URL.revokeObjectURL(url)
  } catch (error) {
    console.error('Download failed:', error)
    alert('Failed to download file.')
  }
}


// -------------------------------------------------------------------
// MIME → extension map (unchanged)
// -------------------------------------------------------------------
const getFileExtensionFromMimeType = (mimeType) => {
  const map = {
    'application/pdf': 'pdf',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
    'application/vnd.ms-excel': 'xls',
    'image/png': 'png',
    'image/jpeg': 'jpg',
    'image/jpg': 'jpg',
    'image/webp': 'webp',
    'image/gif': 'gif',
    'text/csv': 'csv',
    'application/zip': 'zip',
    'application/json': 'json',
  }

  return map[mimeType] || 'bin'
}

export default api
